import { BulkProtectionOptions } from './enums';
import { useStores } from '../components/store';
import { useEffect, useMemo, useState } from 'react';
import { Rule } from '../dtos/version.dto';

export const useActivation = () => {

    const { fwVersionStore, ruleStore } = useStores();

    const {
        GeoIpSrc, GeoIpDest,
        MyNetworkToInternetServices,
        AllowUntrustDest, AllowUntrustSrc,
        BlockUntrustDest, BlockUntrustSrc,
        DropAttBlockListSrc, DropAttBlockListDest,
        AllowInternetToMyNetworkNonWebServices, MyNetworkToInternetAllowedApplications,
        InternetToMyNetworkAllowedServices, AllowMyNetworkToInternetNonWebServices, InternetToMyNetworkAllowedApplications

    } = ruleStore;

    const geoRules = [GeoIpSrc, GeoIpDest];
    const webRule = [MyNetworkToInternetServices];
    const ipAllowListRules = [AllowUntrustDest, AllowUntrustSrc];
    const ipBlockListRules = [BlockUntrustDest, BlockUntrustSrc];
    const servicesRules = [MyNetworkToInternetServices, AllowInternetToMyNetworkNonWebServices, MyNetworkToInternetAllowedApplications,
        InternetToMyNetworkAllowedServices, AllowMyNetworkToInternetNonWebServices, InternetToMyNetworkAllowedApplications];
    const attShieldRules = [DropAttBlockListSrc, DropAttBlockListDest];

    const rulesByPage: { [key in BulkProtectionOptions]: (Rule | null)[] } = useMemo(() => {
        return {
            [BulkProtectionOptions.GEO_FILTERING]: geoRules,
            [BulkProtectionOptions.WEB_FILTERING]: webRule,
            [BulkProtectionOptions.DOMAINS]: webRule,
            [BulkProtectionOptions.ALLOW_LIST]: ipAllowListRules,
            [BulkProtectionOptions.BLOCK_LIST]: ipBlockListRules,
            [BulkProtectionOptions.IP_CONTROL]: [...ipAllowListRules, ...ipBlockListRules],
            [BulkProtectionOptions.APPS]: servicesRules,
            [BulkProtectionOptions.SERVICES]: servicesRules,
            [BulkProtectionOptions.PROTECTION_PLUS]: attShieldRules
        };
    }, [fwVersionStore.selectedFwVersion?.content.policy.rules]);

    const initialPageActivation = Object.values(BulkProtectionOptions).reduce((acc, option) => {
        acc[option] = false;
        return acc;
    }, {} as { [key in BulkProtectionOptions]: boolean });

    const [isPageDisabled, setIsPageDisabled] = useState(initialPageActivation);

    useEffect(() => {
        if (fwVersionStore.selectedFwVersion?.content.policy.rules) {
            calculatePageActivation();
        }
    }, [fwVersionStore.selectedFwVersion?.content.policy.rules]);

    const calculatePageActivation = () => {
        Object.keys(isPageDisabled).forEach((_) => {
            const key = _ as BulkProtectionOptions;
            if (key === BulkProtectionOptions.WEB_FILTERING || key === BulkProtectionOptions.DOMAINS) {
                isPageDisabled[key] = MyNetworkToInternetServices?.disabled || MyNetworkToInternetServices?.profiles.url_filtering === '';
            }
            else {
                const rules = rulesByPage[key];
                isPageDisabled[key] = rules.some(rule => rule?.disabled);
            }
        });
        setIsPageDisabled({ ...isPageDisabled });
    };

    const getPageDisplayName = (pageType: BulkProtectionOptions): string => {
        switch (pageType) {
            case BulkProtectionOptions.GEO_FILTERING:
                return 'Geo Filtering';
            case BulkProtectionOptions.WEB_FILTERING:
                return 'Web Filtering';
            case BulkProtectionOptions.DOMAINS:
                return 'Web Filtering';
            case BulkProtectionOptions.APPS:
                return 'Apps';
            case BulkProtectionOptions.SERVICES:
                return 'Services';
            case BulkProtectionOptions.ALLOW_LIST:
                return 'IP Control Allow List';
            case BulkProtectionOptions.BLOCK_LIST:
                return 'IP Control Block List';
            case BulkProtectionOptions.IP_CONTROL:
                return 'IP Control';
            default:
                return 'ATTDD Shield';
        }
    };

    const getPageType = (pageType: BulkProtectionOptions): string => {
        switch (pageType) {
            case BulkProtectionOptions.GEO_FILTERING:
                return 'COUNTRIES';
            case BulkProtectionOptions.WEB_FILTERING:
                return 'WEB';
            case BulkProtectionOptions.DOMAINS:
                return 'WEB';
            case BulkProtectionOptions.APPS:
                return 'APPS';
            case BulkProtectionOptions.SERVICES:
                return 'SERVICES';
            case BulkProtectionOptions.ALLOW_LIST:
                return 'IP_CONTROL_ALLOW_LIST';
            case BulkProtectionOptions.BLOCK_LIST:
                return 'IP_CONTROL_BLOCK_LIST';
            case BulkProtectionOptions.IP_CONTROL:
                return 'IP_CONTROL';
            default:
                return 'ATTDD_SHIELD';
        }
    };

    const isAllPageRulesDisabled = (pageType: BulkProtectionOptions) => {
        if (pageType === BulkProtectionOptions.WEB_FILTERING || pageType === BulkProtectionOptions.DOMAINS) {
            return MyNetworkToInternetServices?.profiles.url_filtering === '';
        }

        return rulesByPage[pageType].every(_ => _?.disabled);
    };

    const isPartialInactive = (pageType: BulkProtectionOptions) => {
        if (pageType !== BulkProtectionOptions.ALLOW_LIST && pageType !== BulkProtectionOptions.BLOCK_LIST && pageType !== BulkProtectionOptions.IP_CONTROL) {
            return false;
        }
        const pageRules: (Rule | null)[] = rulesByPage[pageType];
        return pageRules.find(_ => _?.disabled) && pageRules.find(_ => !_?.disabled);
    };

    const isOutboundAInactive = (pageType: BulkProtectionOptions) => {
        if (pageType !== BulkProtectionOptions.GEO_FILTERING) return false;
        return GeoIpDest?.disabled;
    };

    const isInboundInactive = (pageType: BulkProtectionOptions) => {
        if (pageType !== BulkProtectionOptions.GEO_FILTERING) return false;
        return GeoIpSrc?.disabled;
    };

    return {
        isPageDisabled,
        isAllPageRulesDisabled,
        isPartialInactive,
        rulesByPage,
        getPageDisplayName,
        getPageType,
        isOutboundActive: isOutboundAInactive,
        isInboundActive: isInboundInactive
    };
};